import { Link } from "@/components/ui/Link";
import type { ReactNode, ButtonHTMLAttributes } from "react";

type ButtonVariant = "primary" | "secondary" | "ghost";

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  href?: string;
  children: ReactNode;
  className?: string;
};

const variants: Record<ButtonVariant, string> = {
  primary:
    "bg-foreground text-background hover:bg-muted hover:text-background",
  secondary:
    "bg-transparent text-foreground ring-1 ring-inset ring-white/15 hover:ring-white/35",
  ghost: "bg-transparent text-foreground hover:text-muted",
};

export function Button({
  variant = "primary",
  href,
  children,
  className = "",
  ...props
}: ButtonProps) {
  const classes = `inline-flex items-center justify-center px-6 py-3 text-sm font-medium tracking-wide transition-all duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground disabled:opacity-50 ${variants[variant]} ${className}`;

  if (href) {
    const linkTitle =
      (typeof children === "string" && children) ||
      (typeof props["aria-label"] === "string" && props["aria-label"]) ||
      href;
    return (
      <Link href={href} className={classes} title={linkTitle}>
        {children}
      </Link>
    );
  }

  return (
    <button type="button" className={classes} {...props}>
      {children}
    </button>
  );
}
